// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: ClickUp's status ring (the status colour, filling up as a task moves
// through its list). Its own file so places outside the Tasks workspace (the
// Agents Time and client panel) can draw it without loading the tasks store.

import styles from '@app/features/konvo_tasks/components/workspace/shared/WorkspaceBits.module.css';
import type {KonvoTaskListStatus, KonvoTaskStatus} from '@app/features/konvo_tasks/state/KonvoTasks';
import {CheckIcon} from '@app/features/ui/icons/Icons';
import {clsx} from 'clsx';
import type React from 'react';

export const FALLBACK_COLOR = '#87909f';

export function isClosedStatus(status: Pick<KonvoTaskStatus, 'type'>): boolean {
	return status.type === 'closed' || status.type === 'done';
}

/**
 * How far along its list a status is, 0 (first/open) to 1 (closed), from the
 * list's statuses when we have them; without them 'custom' shows half full.
 */
export function statusProgress(status: KonvoTaskStatus, statuses?: Array<KonvoTaskListStatus>): number {
	if (isClosedStatus(status)) return 1;
	if (status.type === 'open') return 0;
	const open = (statuses ?? []).filter((entry) => !isClosedStatus(entry));
	const index = open.findIndex((entry) => (entry.status ?? '').toLowerCase() === (status.status ?? '').toLowerCase());
	if (index < 0 || open.length < 2) return 0.5;
	return Math.min(0.85, Math.max(0.25, index / (open.length - 1)));
}

export function StatusRing({
	status,
	statuses,
	progress,
	size = 18,
	className,
}: {
	status: KonvoTaskStatus;
	statuses?: Array<KonvoTaskListStatus>;
	/** How far along, 0 to 1, when the server worked it out from the list (instead of `statuses`). */
	progress?: number | null;
	size?: number;
	className?: string;
}) {
	const done = isClosedStatus(status);
	const fill = Math.round((done ? 1 : (progress ?? statusProgress(status, statuses))) * 100);
	return (
		<span
			className={clsx(styles.ring, done && styles.ringDone, className)}
			style={
				{
					'--status-color': status.color ?? FALLBACK_COLOR,
					'--status-fill': `${fill}%`,
					'--ring-size': `${size}px`,
				} as React.CSSProperties
			}
			aria-hidden
		>
			{done && <CheckIcon className={styles.ringCheck} weight="bold" />}
		</span>
	);
}
